import { UnexpectedError } from '../errors/UnexpectedError';
import { Unit, unitToString } from '../unit';
import assertNever from '../utils/assertNever';

export type StringType = {
  ty: 'string';
};

export type BooleanType = {
  ty: 'boolean';
};

// A number is either unitless or has a typecheck-time known unit. It is
// represented at runtime by a number.
export type NumberType = {
  ty: 'number';
  unit?: Unit;
};

// A quantity is a number and a unit. The unit is not known at typecheck-time,
// though it is known to be one of the listed options. A quantity is represented
// at runtime by a "fat" value: in JavaScript by the Quantity class, and in SQL
// by a pair of columns (one for the value, one for the unit).
export type QuantityType = {
  ty: 'quantity';
  units: Array<Unit>;
};

// Our list may only contain elements of primitive types.
export type ListType = {
  ty: 'list';
  elemTy: StringType | BooleanType | NumberType;
};

export type NullableType = {
  ty: 'nullable';
  baseTy:
    | StringType
    | BooleanType
    | NumberType
    | QuantityType
    | DateType
    | DatetimeType;
};

export type NullType = {
  ty: 'null';
};

export type DateType = {
  ty: 'date';
};

export type DatetimeType = {
  ty: 'datetime';
};

export type ExprType =
  | StringType
  | BooleanType
  | NumberType
  | QuantityType
  | ListType
  | NullableType
  | NullType
  | DateType
  | DatetimeType;

export function stringifyType(ty: ExprType): string {
  switch (ty.ty) {
    case 'string':
    case 'boolean':
    case 'null':
    case 'date':
    case 'datetime':
      return ty.ty;
    case 'number':
      if (ty.unit) {
        return `number[${unitToString(ty.unit)}]`;
      }
      return ty.ty;
    case 'quantity':
      const options = ty.units.map(unitToString).join(', ');
      return `quantity with options: ${options}`;
    case 'list':
      return `list of ${stringifyType(ty.elemTy)}`;
    case 'nullable':
      switch (ty.baseTy.ty) {
        case 'string':
        case 'boolean':
        case 'date':
        case 'datetime':
          return `${ty.baseTy.ty}?`;
        case 'number':
          if (ty.baseTy.unit) {
            return `number?[${unitToString(ty.baseTy.unit)}]`;
          } else {
            return 'number?';
          }
        case 'quantity':
          return 'nullable ' + stringifyType(ty.baseTy);
        default:
          assertNever(ty.baseTy);
      }
    default:
      assertNever(ty);
  }
}

export function makeNullable(ty: ExprType): ExprType {
  if (ty.ty === 'nullable' || ty.ty === 'null') {
    return ty;
  } else if (
    ty.ty === 'quantity' ||
    ty.ty === 'number' ||
    ty.ty === 'string' ||
    ty.ty === 'boolean' ||
    ty.ty === 'date' ||
    ty.ty === 'datetime'
  ) {
    return { ty: 'nullable', baseTy: ty };
  } else {
    throw new UnexpectedError(`Can't make ${ty.ty} nullable`);
  }
}

export function canBeNull(ty: ExprType): ty is NullableType | NullType {
  return ty.ty === 'nullable' || ty.ty === 'null';
}

export function isNonNullable(ty: ExprType): boolean {
  return ty.ty !== 'nullable' && ty.ty !== 'null';
}

export function isNullable(ty: ExprType): boolean {
  return ty.ty === 'nullable' || ty.ty === 'null';
}

export function makeNonNullable(ty: ExprType): ExprType {
  if (ty.ty === 'nullable') {
    return ty.baseTy;
  }
  return ty;
}
